<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import UserName from '@/components/UserName.vue'
import { getMonthName } from '@/lib/dates'
import { podiumClass } from '@/lib/podium'

/**
 * General standings of the running season: the block the standings page shows next to the overall
 * ranking (see racing_events_season.md).
 *
 * It only renders the season it is given — the request, the missing season and the errors stay with
 * the caller (see StandingsView) — so the block can be moved wherever the same payload arrives,
 * like the ranking table and the recent races. Until there is a season to write, the block is its
 * own placeholder: it is rendered from the first paint (the caller knows whether one is worth
 * waiting for) so the page does not move when the season lands.
 */
const props = defineProps({
  /** Season block of the standings payload (GET /api/standings, key `season`); null until it lands. */
  season: { type: Object, default: null },
  /** Rows the table will hold, so the placeholder has the height of the table it replaces. */
  rows: { type: Number, default: 20 },
})

const { t } = useI18n()

const event = computed(() => props.season?.event ?? null)
const standings = computed(() => props.season?.standings ?? [])

/**
 * The season has a name of its own ("Championship of September"), built from the type and the month
 * its window opens in — the same title the event card shows.
 */
const title = computed(() => t(`eventNames.${event.value.eventType}`, {
  month: getMonthName(event.value.dateStart),
}))

/** Where the header and the footer lead: the event page of the season. */
const eventLink = computed(() => ({
  name: 'event_detail',
  params: { id: event.value.id },
}))

/** "1-20 of 37": the slice on the screen out of the whole season. */
const range = computed(() => ({
  from: standings.value.length ? 1 : 0,
  to: standings.value.length,
  total: props.season?.total ?? standings.value.length,
}))

/**
 * Same shape as the block itself, header included: nothing on the page moves when the season lands
 * (see v-skeleton-loader in RankingTable).
 */
const skeletonType = computed(() => `list-item-avatar-two-line, table-thead, table-row@${props.rows}`)
</script>

<template>
  <v-card elevation="3" rounded="lg"
    class="d-flex flex-column overflow-hidden"
  >
    <!-- Nothing to write yet: the placeholder keeps the place of the whole block, header included. -->
    <v-skeleton-loader
      v-if="!event"
      :type="skeletonType"
      :loading-text="t('standings.loading')"
    />

    <template v-else>
      <RouterLink
        v-ripple
        :to="eventLink"
        class="header-link d-flex align-center ga-3 pa-4 text-decoration-none text-high-emphasis"
      >
        <v-avatar color="purple" variant="tonal" size="48">
          <v-icon size="28">mdi-calendar-star</v-icon>
        </v-avatar>
        <div class="flex-grow-1">
          <h2 class="text-h6 font-weight-bold">{{ title }}</h2>
          <!--p class="text-caption text-medium-emphasis mb-0">{{ t('standings.seasonRules') }}</p>-->
        </div>
      </RouterLink>

      <v-divider />

      <div v-if="!standings.length" class="text-center pa-8">
        <v-icon size="56" color="medium-emphasis" class="mb-3">mdi-calendar-star</v-icon>
        <p class="text-medium-emphasis mb-0">{{ t('events.noStandings') }}</p>
      </div>

      <template v-else>
        <v-table hover>
          <thead>
            <tr>
              <th class="text-center" style="width: 64px">{{ t('standings.position') }}</th>
              <th>{{ t('events.player') }}</th>
              <!-- The season is decided on points, with the tracks won as the first tie-break. -->
              <th class="text-center">{{ t('events.points') }}</th>
              <th class="text-center">{{ t('events.wins') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="player in standings" :key="player.userId" :class="{ 'me-row': player.isMe }">
              <td class="text-center">
                <v-icon v-if="player.position <= 3" :class="podiumClass(player.position)" size="20">mdi-medal</v-icon>
                <span v-else class="text-medium-emphasis">{{ player.position }}</span>
              </td>
              <td class="font-weight-medium"><UserName :name="player.playerName" /></td>
              <td class="text-center font-weight-bold">{{ player.points ?? '–' }}</td>
              <td class="text-center">
                <span v-if="player.wins">{{ player.wins }}</span>
                <span v-else class="text-medium-emphasis">–</span>
              </td>
            </tr>
          </tbody>
        </v-table>

        <!-- The footer sits at the bottom of the card, whatever the table holds: the two blocks on
             the page end on the same line (see the columns in StandingsView). -->
        <v-divider class="mt-auto" />

        <div class="d-flex flex-wrap align-center ga-3 pa-4">
          <span class="text-caption text-medium-emphasis">
            {{ t('standings.range', { from: range.from, to: range.to, total: range.total }) }}
          </span>
          <v-spacer />
          <!-- The footer repeats the way out, where the eye lands after the table. -->
          <v-btn
            :to="eventLink"
            variant="text"
            size="small"
            color="primary"
            append-icon="mdi-chevron-right"
          >
            {{ t('standings.goToEvent') }}
          </v-btn>
        </div>
      </template>
    </template>
  </v-card>
</template>

<style scoped>
/* `v-ripple` is placed inside the element: it needs one that is positioned. */
.header-link {
  position: relative;
}

.header-link:hover {
  background-color: rgba(var(--v-theme-on-surface), 0.06);
}

/* The logged-in player stands out without breaking the row rhythm of the table. */
.me-row td {
  background-color: rgba(var(--v-theme-primary), 0.06);
}
</style>
